import { FC, PropsWithChildren, useEffect, useMemo } from "react";

import { Flex, MenuItem } from "@chakra-ui/react";
import { IconUserCircle, IconSettings } from "@tabler/icons-react";
import { ClickableTopNavItem, Page as CorePage, PageProps as CorePageProps } from "am-tax-fe-core";
import { useLocation, useNavigate, useMatch } from "react-router-dom";

import { useClientEngagements, useCurrentUser, useEngagement, useProject } from "../api";
import { useBanners } from "../api/admin/api-queries.ts";
import { GlobalEngageTopBanner, GlobalEngageTopBannerProps } from "../components/GlobalEngageBanner/GlobalEngageTopBanner.tsx";
import { useHasAccess } from "../hooks";
import { AccessStatus } from "../types/enums/AccessStatus.ts";

export type PageProps = PropsWithChildren<Omit<CorePageProps, "location" | "menuItems">> & { subTitle?: string | undefined };

export const Page: FC<PageProps> = ({ children, subTitle, ...pageProps }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { isDeveloper } = useHasAccess();

    const menuItems = useMemo(() => {
        const items = [
            <MenuItem
                key={"myAccount"}
                icon={<IconUserCircle />}
                onClick={() => {
                    navigate("/myAccount");
                }}
            >
                My Account
            </MenuItem>,
        ];
        if (isDeveloper) {
            items.push(
                <MenuItem key={"profile"} icon={<IconSettings />} onClick={() => navigate("/profile")}>
                    Profile Settings
                </MenuItem>,
            );
        }
        return items;
    }, [isDeveloper, navigate]);

    const matchesEngagement = useMatch("/engagement/:engagementId/*");
    const engagementId = matchesEngagement?.params?.engagementId;

    const matchesProject = useMatch("/engagement/:engagementId/project/:projectId/*");
    const projectId = matchesProject?.params?.projectId;

    const { data: banners } = useBanners();
    const engagementQuery = useEngagement(engagementId, projectId);
    const engagement = engagementQuery.data;

    const projectQuery = useProject(engagementId, projectId);
    const project = projectQuery.data;

    // get engagement for project
    const clientId = engagement?.clientId || project?.clientId;
    const { data } = useClientEngagements(clientId);
    const clientEngagement = data?.engagements?.find(x => x.engagementId === engagementId);

    const { data: currentUser } = useCurrentUser();

    const breadcrumbs: ClickableTopNavItem[] = [];

    let engagementBreadcrumb: ClickableTopNavItem | undefined;

    if (engagement) {
        engagementBreadcrumb = {
            name: `${engagement.projectCode}-${engagement.name}`,
            onClick: () => navigate(`/engagement/${engagement.engagementId}/dashboard`),
        };
    } else if (project && clientEngagement) {
        engagementBreadcrumb = {
            name: `${clientEngagement.projectCode}-${clientEngagement.name}`,
            onClick: () => {
                if (clientEngagement.accessStatus === AccessStatus.Accepted) navigate(`/engagement/${clientEngagement.engagementId}/dashboard`);
            },
        };
    }

    if (engagementBreadcrumb) {
        breadcrumbs.push(engagementBreadcrumb);
    }

    let projectBreadcrumb: ClickableTopNavItem | undefined;
    if (project) {
        projectBreadcrumb = {
            name: `${project.projectCode}-${project.name}`,
            onClick: () => navigate(`/engagement/${project.engagementId}/project/${project.projectId}/dashboard`),
        };
    }
    if (projectBreadcrumb) {
        breadcrumbs.push(projectBreadcrumb);
    }
    const topBanner = banners?.find(banner => banner.bannerType === 2);

    const showGlobalEngageTopBanner = useMemo(() => {
        if (topBanner) {
            if (topBanner.isInternal === true) {
                const currentUserIsInternal = currentUser?.isInternal;
                if (!currentUserIsInternal) {
                    return false;
                }
            }
            const currentDate = new Date();
            const startDate = topBanner.startDate ? new Date(topBanner.startDate) : undefined;
            const endDate = topBanner.endDate ? new Date(topBanner.endDate) : undefined;

            if (!!startDate && currentDate >= startDate && !!endDate && currentDate <= endDate) {
                return true;
            }
        }
        return false;
    }, [topBanner, currentUser]);

    const topBannerProps: GlobalEngageTopBannerProps = {
        description: topBanner?.description ?? "",
        engageLink: topBanner?.engageLink ?? "",
        infoLink: topBanner?.infoLink ?? "",
        infoLinkText: topBanner?.infoLinkText ?? "",
        note: topBanner?.note ?? "",
        title: topBanner?.title ?? "",
        engageLinkText: topBanner?.engageLinkText ?? "",
        supportingButtonLink: topBanner?.videoLink ?? "",
        supportingButtonText: topBanner?.videoLinkText ?? "",
    };

    useEffect(() => {
        const isDevelopment = import.meta.env.VITE_ENGAGE_ENV === "development";
        const domainId = isDevelopment ? import.meta.env.VITE_ONE_TRUST_DEV_DOMAIN_ID : import.meta.env.VITE_ONE_TRUST_PROD_DOMAIN_ID;
        // Create and append the first script
        const otAutoBlockScript = document.createElement("script");
        Object.defineProperty(otAutoBlockScript, "src", {
            value: `https://cdn.cookielaw.org/consent/${domainId}/OtAutoBlock.js`,
            configurable: true,
        });
        otAutoBlockScript.type = "text/javascript";
        document.head.appendChild(otAutoBlockScript);

        // Create and append the second script
        const otSDKStubScript = document.createElement("script");
        otSDKStubScript.type = "text/javascript";
        otSDKStubScript.src = "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js";
        otSDKStubScript.setAttribute("data-domain-script", domainId!);
        document.head.appendChild(otSDKStubScript);

        // Create and append the inline script
        const optanonWrapperScript = document.createElement("script");
        optanonWrapperScript.type = "text/javascript";
        optanonWrapperScript.innerHTML = "function OptanonWrapper() { }";
        document.head.appendChild(optanonWrapperScript);

        // Cleanup function to remove the scripts when the component unmounts
        return () => {
            document.head.removeChild(otAutoBlockScript);
            document.head.removeChild(otSDKStubScript);
            document.head.removeChild(optanonWrapperScript);
        };
    }, []);

    return (
        <Flex direction="column" minHeight="100vh">
            {showGlobalEngageTopBanner && <GlobalEngageTopBanner {...topBannerProps} />}

            <CorePage
                location={location}
                topNavProps={{
                    buildNumber: import.meta.env.VITE_BUILD_NUMBER,
                    breadcrumbs,
                    subTitle,
                }}
                menuItems={menuItems}
                {...pageProps}
            >
                {children}
            </CorePage>
        </Flex>
    );
};
